// Renders an HTML string to a downloadable PDF blob (no new windows).
// Uses html2canvas + jsPDF so the download is direct and works inside PWAs
// and Capacitor webviews where popups are usually blocked.
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function htmlToPdfDownload(
  html: string,
  filename: string,
  opts?: { width?: number },
): Promise<void> {
  const width = opts?.width ?? 780;
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.left = "-99999px";
  container.style.top = "0";
  container.style.width = `${width}px`;
  container.style.background = "#ffffff";
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    // Wait for fonts + images
    if ((document as any).fonts?.ready) {
      try { await (document as any).fonts.ready; } catch { /* noop */ }
    }
    const imgs = Array.from(container.querySelectorAll("img"));
    await Promise.all(
      imgs.map(
        (img) =>
          new Promise<void>((resolve) => {
            if ((img as HTMLImageElement).complete) return resolve();
            img.addEventListener("load", () => resolve(), { once: true });
            img.addEventListener("error", () => resolve(), { once: true });
          }),
      ),
    );

    const canvas = await html2canvas(container, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
    });
    const pdf = new jsPDF({ orientation: "portrait", unit: "px", format: "a4", compress: true });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgProps = { width: canvas.width, height: canvas.height };
    const ratio = pageWidth / imgProps.width;
    const scaledH = imgProps.height * ratio;

    let position = 0;
    let remaining = scaledH;
    const imgData = canvas.toDataURL("image/jpeg", 0.95);

    if (scaledH <= pageHeight) {
      pdf.addImage(imgData, "JPEG", 0, 0, pageWidth, scaledH);
    } else {
      // Multi-page: paginate by clipping via negative y-offset
      while (remaining > 0) {
        pdf.addImage(imgData, "JPEG", 0, position, pageWidth, scaledH);
        remaining -= pageHeight;
        if (remaining > 0) {
          pdf.addPage();
          position -= pageHeight;
        }
      }
    }

    const blob = pdf.output("blob");
    downloadBlob(blob, filename.endsWith(".pdf") ? filename : `${filename}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
}
